import * as THREE from 'three';
import { EWall } from '../../@types/Enums';
import { CM_TO_INTERNAL_FACTOR, GLTF_FONT_SIZE_SCALE_FACTOR, GLTF_IMPORT_SCALE_FACTOR } from '../../utils/constants';
import { getProductIdByName } from '../../utils/oldPlannerImportProductLookupTable';
import {
  Gltf, IGltfDoor, IGltfElement, IGltfFloorplan, IGltfModel2D,
  IGltfPartitionalWall, IGltfTextField, IGltfWall,
  IGltfWallPoint, IGltfWindow
} from '../@types/Gltf';
import { scalePoint } from '../utils/scaling/scalePoint';
import { scaleRoomCorners } from '../utils/scaling/scaleRoomCorners';
import { extractPositionValues } from '../utils/valueExtraction/extractPositionValues';
import { extractWallEndpoints, MatrixData } from '../utils/valueExtraction/extractWallEndpoints';

// Set of special 2D models that are not currently supported in the new floor planner.
export const specialModelSrcSet = new Set([
  '/img/sampleProducts/gas-blockheizkraftwerk_weiss.png',
  '/img/sampleProducts/gasheizherd_weiss.png',
  '/img/sampleProducts/gas-heizkessel_weiss.png',
  '/img/sampleProducts/gas-waermepumpe_weiss.png',
  '/img/sampleProducts/klimageraet_weiss.png',
  '/img/sampleProducts/waschmaschine_weiss.png',
  '/img/sampleProducts/waeschetrockner_weiss.png',
  '/img/sampleProducts/umwaelzpumpe_weiss.png',
  '/img/sampleProducts/wasserspeicher.png',
]);

/**
 * Function to extract all data from the exported floorplan of the old planner.
 * Will allow us to import the files into the new floorplanner after additional conversion.
 *
 * @param gltfJson the gltf file that was exported by the old floorplanner.
 * @returns {IGltfFloorplan} object containing all relevant data to reconstruct the floorplan.
 *
 */
// eslint-disable-next-line complexity
export const gltfToStructure = (gltfJson: Gltf): IGltfFloorplan => {
  // Constructing result collections
  const elements: Array<IGltfElement> = [];
  const models2D: Array<IGltfModel2D> = [];
  const partitionalWalls: Array<IGltfPartitionalWall> = [];
  const textFields: Array<IGltfTextField> = [];

  // Extracting relevant floor data
  const extras = gltfJson.scenes[0].extras;
  const isExtrasEmpty = !extras || Object.keys(extras).length === 0; // if extras is empty, use undefined for all floor values
  
  const floorRotation   = isExtrasEmpty ? 0 : extras.floorTextureRotation;
  const floorTexturePath = isExtrasEmpty ? "" : extras.floorTexturePath;
  const floorTextureType = isExtrasEmpty ? undefined : extras.floorTextureType;
  
  const tileLength = isExtrasEmpty ? 0 : ((extras.floorStructure[0] as number) * 10) / 2;
  const tileWidth  = isExtrasEmpty ? 0 : ((extras.floorStructure[1] as number) * 10) / 2;
  const tileColor  = isExtrasEmpty ? "" : (extras.floorStructure[2] as string);
  const tileGap    = isExtrasEmpty ? 0 : ((extras.floorStructure[3] as number) * 10) / 4;
  const gapColor   = isExtrasEmpty ? "" : (extras.floorStructure[4] as string);
  
  // Working objects
  const oldRoomCorners: Array<IGltfWallPoint> = [];

  // Construction of the result object
  const result: IGltfFloorplan = {
    walls: [] as IGltfWall[],
    floor: {
      tileLength: tileLength,
      tileWidth: tileWidth,
      tileColor: tileColor,
      tileGap: tileGap,
      gapColor: gapColor,
      floorRotation: floorRotation,
      floorTexturePath: floorTexturePath,
      floorTextureType: floorTextureType,
    },
    elements: [],
    models2D: [],
    partionalWalls: [],
    textFields: [],
  };

  // Extracting the corners of the room before scaling to match the different coordinate system of the new planner.
  for (let c = 0; c < gltfJson.nodes.length; c++) {
    const relatedNode = gltfJson.nodes[c]; // The related node object, that could be anything; Wall, elements, etc.
    if (relatedNode.extras === undefined) continue; // The extra field must exist; otherwise, the node is not relevant for data extraction.

    // Wall handling: A normal wall appears if the normal field exists.
    if (relatedNode.extras.normal !== undefined) {
      const meshNr = relatedNode.mesh as number;
      const name = relatedNode.extras?.Wall as string;

      const pointX = gltfJson.meshes[meshNr].primitives[0].extras?.point1x as number;
      const pointY = gltfJson.meshes[meshNr].primitives[0].extras?.point1y as number;

      const rotatedX = pointX;
      const rotatedY = -pointY;

      oldRoomCorners.push({
        point: new THREE.Vector3(rotatedX, rotatedY, 0),
        wallName: name,
      });
    }

    // Extracting position values from transformation matrix
    const positionValues = extractPositionValues(relatedNode.matrix);
    if (!positionValues) continue; // Node element is broken -> continue with next element

    // Scaling position (old floorplan -> new floorplan dimensions)
    const unscaledPosition = new THREE.Vector3(positionValues.xPos, positionValues.yPos);
    const scaledPosition = scalePoint(unscaledPosition, GLTF_IMPORT_SCALE_FACTOR);

    // Relevant position values
    const xPos = scaledPosition.x;
    const yPos = scaledPosition.y;

    let rotation = positionValues.rotation;
    // Convert to positive value if negative
    if (rotation < 0) {
      rotation += 2 * Math.PI;
    }

    // Extract possible values
    const product = relatedNode.extras.product ?? [];
    const widht = ((relatedNode.extras.width ?? 0) * 10) / 2;
    const height = ((relatedNode.extras.height ?? 0) * 10) / 2;
    const length = ((relatedNode.extras.length ?? 0) * 10) / 2;
    const src = relatedNode.extras.src ?? '';
    const type = relatedNode.extras.type ?? '';
    const description = relatedNode.extras.description ?? '';
    const color = relatedNode.extras.color ?? '#000000';
    const text = relatedNode.extras.text ?? '';
    const isText = relatedNode.extras.isText;
    const isTextField = relatedNode.extras.isTextField;

    // CASE: Node is element, 2DModel or partitional Wall
    if (relatedNode.extras.src !== undefined) {
      // CASE: Node is element
      if (relatedNode.extras.src === '/img/sampleProducts/heizung2.png' || relatedNode.extras.src === '/img/sampleProducts/heizung3.png') {
        // Special case for elements: The position is handled differently.
        const element: IGltfElement = {
          product: product,
          depth: widht * CM_TO_INTERNAL_FACTOR,
          height: height * CM_TO_INTERNAL_FACTOR,
          length: length * CM_TO_INTERNAL_FACTOR,
          src: src,
          type: type,
          description: description,
          color: color,
          xPos: xPos,
          yPos: yPos,
          rotation: rotation,
        };
        elements.push(element);
      }

      // Case: Element is a special 2DModel
      if (specialModelSrcSet.has(relatedNode.extras.src)) {
        elements.push({
          product,
          depth: widht * CM_TO_INTERNAL_FACTOR,
          height: height * CM_TO_INTERNAL_FACTOR,
          length: length * CM_TO_INTERNAL_FACTOR,
          src,
          type,
          description: `${product[3]}${description ? ' - ' + description : ''}`,
          color,
          xPos,
          yPos,
          rotation,
        } as IGltfElement);
      }

      // CASE: Node is partitional wall
      if (relatedNode.extras.src === '/img/sampleProducts/wallBlack.png' && relatedNode.matrix) {
        // Creating a data object for wall extraction.
        const data: MatrixData = {
          matrix: relatedNode.matrix,
          extras: {
            length: relatedNode.extras.length ?? 0,
            depth: widht,
          },
        };

        // Calculating the partional Wall points
        const wallPoints = extractWallEndpoints(data);
        if (!wallPoints) continue; // Data is broken -> continue with next entry

        // Extracting wall points
        const partWallStartX = wallPoints.start[0];
        const partWallStartY = wallPoints.start[1];
        const partWallEndPointX = wallPoints.end[0];
        const partWallEndPointY = wallPoints.end[1];

        const partitionWall: IGltfPartitionalWall = {
          product: product,
          depth: widht * CM_TO_INTERNAL_FACTOR, // the dimension are different handled for walls
          height: 0, // dummy data
          length: length * CM_TO_INTERNAL_FACTOR,
          src: src,
          type: type,
          description: description,
          color: color,
          startX: partWallStartX,
          startY: partWallStartY,
          endX: partWallEndPointX,
          endY: partWallEndPointY,
          rotation: rotation,
        };
        partitionalWalls.push(partitionWall);
      }

      // CASE: Checking if src is pointing to a 2D model.
      const relatedFileId = getProductIdByName(relatedNode.extras.src);
      if (relatedFileId) {
        const model2D: IGltfModel2D = {
          toFileId: relatedFileId, // Extended field -> pointing to fileId (FilesAPI)
          product: product,
          depth: widht,
          height: height,
          length: length,
          src: src,
          type: type,
          description: description,
          color: color,
          xPos: xPos,
          yPos: yPos,
          rotation: rotation,
        };
        models2D.push(model2D);
      }
    }

    // CASE: Node is Textfield
    if (isText && isTextField) {
      // Special case: Color is handled differently for text fields.
      const textFieldColor = Number(color) === 0 ? '#000000' : color;

      // Dynamically determine font size based on transformation scale
      const baseFontSize = relatedNode.extras.fontSize ?? 1;
      const fontSize = baseFontSize * positionValues.scaleX;
      const textLength = relatedNode.extras.text?.length ?? 1;

      // Calculate offsets dynamically based on text length and scaling
      const offsetX = fontSize * textLength * positionValues.scaleX * GLTF_FONT_SIZE_SCALE_FACTOR * 1.5;
      const offsetY = fontSize * positionValues.scaleY * GLTF_FONT_SIZE_SCALE_FACTOR * 4;

      // Scaling font
      const scaledFontSize = fontSize * GLTF_FONT_SIZE_SCALE_FACTOR;
      const tmpVector = new THREE.Vector3(positionValues.xPos + offsetX, positionValues.yPos - offsetY);
      const scaledTextFieldPoint = scalePoint(tmpVector, GLTF_IMPORT_SCALE_FACTOR);

      const textField: IGltfTextField = {
        isText: isText,
        isTextField: isTextField,
        text: text,
        fontSize: scaledFontSize,
        color: textFieldColor,
        xPos: scaledTextFieldPoint.x, // Special case
        yPos: scaledTextFieldPoint.y, // Special case
        rotation: rotation,
      };
      textFields.push(textField);
    }
  }

  // Scaling them down to match our internal units.
  const scaledDownCorners = scaleRoomCorners(oldRoomCorners, GLTF_IMPORT_SCALE_FACTOR, new THREE.Vector3(0, 0, 0));

  // We iterate over the nodes array to locate the first wall again and set its parameters.
  let wallIndex = 0;
  for (let i = 0; i < gltfJson.nodes.length; i++) {
    if (gltfJson.nodes[i].extras !== undefined) {
      if (gltfJson.nodes[i].extras?.normal !== undefined) {
        const tmpLength = (gltfJson.nodes[i].extras?.length as number) * CM_TO_INTERNAL_FACTOR;
        const tmpWallRef = gltfJson.nodes[i].extras?.Wall as string;
        let wallX = 0;
        let wallY = 0;
        // Matching the wall reference string to our scaled up corners.
        for (let s = 0; s < scaledDownCorners.length; s++) {
          if (scaledDownCorners[s].wallName === tmpWallRef) {
            wallX = scaledDownCorners[s].point.x;
            wallY = scaledDownCorners[s].point.y;
            break;
          }
        }

        // Temporary Array for collecting doors and windows
        const tmpDoors: Array<IGltfDoor> = [];
        const tmpWindows: Array<IGltfWindow> = [];

        // Window and Door Handling
        // Then we traverse the array again and check for matching wall identifiers to extract windows and doors as well.
        for (let o = 0; o < gltfJson.nodes.length; o++) {
          if (gltfJson.nodes[o].extras !== undefined) {
            if (gltfJson.nodes[o].extras?.normal === undefined) {
              if (gltfJson.nodes[o].extras?.Wall === tmpWallRef) {
                if (gltfJson.nodes[o].extras?.DoorOrientation !== undefined) {
                  const tmpDoor: IGltfDoor = {
                    wallIndex: wallIndex,
                    distanceFromWall: (gltfJson.nodes[o].extras?.DistanceFromWall as number) / 2,
                    orientation: gltfJson.nodes[o].extras?.DoorOrientation as 'Inside' | 'Outside',
                    openingDirection: gltfJson.nodes[o].extras?.DoorOpeningDirection as 'LeftToRight' | 'RightToLeft',
                    width: (gltfJson.nodes[o].extras?.Width as number) / 2,
                  };
                  tmpDoors.push(tmpDoor);
                } else {
                  const tmpWindow: IGltfWindow = {
                    wallIndex: wallIndex,
                    distanceFromWall: (gltfJson.nodes[o].extras?.DistanceFromWall as number) / 2,
                    width: (gltfJson.nodes[o].extras?.Width as number) / 2,
                  };
                  tmpWindows.push(tmpWindow);
                }
              }
            }
          }
        }

        // Temporary wall gets pushed to the return data and we increase the index.
        result.walls.push({
          x: wallX,
          y: wallY,
          length: tmpLength,
          index: wallIndex,
          doors: tmpDoors,
          windows: tmpWindows,
          type: EWall.STANDARD,
        });
        wallIndex++;
      }
    }
  }
  result.elements = elements;
  result.models2D = models2D;
  result.partionalWalls = partitionalWalls;
  result.textFields = textFields;
  return result;
};
